<template>
  <EmaAgentLayout
    title="设备管理智能体"
    description="“故障诊断 · 维修指导 · 运营报告 · 趋势分析 · 设备巡检”全流程，实现设备全生命周期“一站式”智能服务"
    :logo="agentLogo"
    :drawer-open="drawerVisible"
  >
    <div class="ema-board">
      <div class="section-title">设备态势概览</div>

      <div class="overview-stats">
        <div
          v-for="stat in overviewStats"
          :key="stat.label"
          class="overview-stat-card"
          :class="{ clickable: stat.clickable }"
          @click="handleStatClick(stat)"
        >
          <span class="overview-stat-icon" :class="stat.type">
            <i :class="stat.icon"></i>
          </span>
          <div class="overview-stat-info">
            <div class="overview-stat-value">
              {{ stat.value }}
              <em v-if="stat.delta" class="overview-stat-delta">{{ stat.delta }}</em>
            </div>
            <div class="overview-stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>

      <div class="chart-grid">
        <section class="scene-card chart-card">
          <div class="card-head">
            <div class="card-title-wrap">
              <span class="feature-icon diagnosis"><i class="el-icon-first-aid-kit"></i></span>
              <h3>设备 AI 诊断</h3>
            </div>
            <div class="card-head-actions">
              <span class="period-tag">近7天</span>
              <button type="button" class="link-pill" @click="sendAISummary('diagnosis')">
                AI 总结 <i class="el-icon-right"></i>
              </button>
            </div>
          </div>

          <div class="metric-boxes">
            <div class="metric-box">
              <span class="metric-label">诊断总次数</span>
              <strong class="metric-value">{{ formatNumber(diagnosisSummary.total) }}</strong>
            </div>
            <div class="metric-box">
              <span class="metric-label"><i class="dot success"></i>正常</span>
              <strong class="metric-value success">{{ formatNumber(diagnosisSummary.normal) }}</strong>
            </div>
            <div class="metric-box">
              <span class="metric-label"><i class="dot danger"></i>异常</span>
              <strong class="metric-value danger">{{ formatNumber(diagnosisSummary.abnormal) }}</strong>
            </div>
          </div>

          <div class="chart-panel">
            <div class="chart-panel-header">
              <div class="chart-panel-title">诊断趋势</div>
            </div>
            <div ref="diagnosisChartRef" class="echart-container"></div>
          </div>
        </section>

        <section class="scene-card chart-card">
          <div class="card-head">
            <div class="card-title-wrap">
              <span class="feature-icon health"><i class="el-icon-pie-chart"></i></span>
              <h3>健康度分布</h3>
            </div>
            <div class="card-head-actions">
              <span class="period-tag">近7天</span>
              <button type="button" class="link-pill" @click="sendAISummary('health')">
                AI 总结 <i class="el-icon-right"></i>
              </button>
            </div>
          </div>

          <div class="metric-boxes metric-boxes-2">
            <div class="metric-box">
              <span class="metric-label">设备总数</span>
              <strong class="metric-value">{{ healthSummary.total }}</strong>
            </div>
            <div class="metric-box">
              <span class="metric-label"><i class="dot success"></i>平均健康分</span>
              <strong class="metric-value success">{{ healthSummary.avg }}</strong>
            </div>
          </div>

          <div class="chart-panel">
            <div class="chart-panel-header">
              <div class="chart-panel-title">健康分布</div>
              <div class="chart-panel-legend">
                <span v-for="bar in healthBars" :key="bar.label">
                  <i class="dot" :style="{ background: bar.color }"></i>{{ bar.label }}
                </span>
              </div>
            </div>
            <div ref="healthChartRef" class="echart-container"></div>
          </div>
        </section>
      </div>

      <div class="record-grid">
        <section class="scene-card record-card">
          <div class="card-head">
            <div class="card-title-wrap">
              <span class="feature-icon small"><i class="el-icon-first-aid-kit"></i></span>
              <div>
                <h3>AI 诊断记录</h3>
                <p>最近一周 · 共 {{ formatNumber(diagnosisSummary.total) }} 次</p>
              </div>
            </div>
            <button type="button" class="view-all-link" @click="openDrawer('AI 诊断')">
              查看全部 <i class="el-icon-right"></i>
            </button>
          </div>
          <div class="record-list">
            <div v-for="(item, idx) in diagnosisRecords" :key="item.id + '-' + idx" class="record-item">
              <span class="record-status-icon" :class="item.statusType">
                <i :class="item.icon"></i>
              </span>
              <div class="record-main">
                <strong>{{ item.device }}</strong>
                <p>{{ item.issue }}</p>
              </div>
              <button type="button" class="text-link" @click="openRecordDetail('diagnosis', item)">
                查看详情 <i class="el-icon-right"></i>
              </button>
            </div>
          </div>
        </section>

        <section class="scene-card record-card">
          <div class="card-head">
            <div class="card-title-wrap">
              <span class="feature-icon small"><i class="el-icon-search"></i></span>
              <div>
                <h3>AI 巡检记录</h3>
                <p v-if="inspectionSummary.totalCount > 0 || inspectionRecords.length > 0">
                  近一周 · 共 {{ inspectionSummary.totalCount || inspectionRecords.length }} 次
                </p>
                <p v-else>加载中...</p>
              </div>
            </div>
            <button type="button" class="view-all-link" @click="openDrawer('AI 巡检')">
              查看全部 <i class="el-icon-right"></i>
            </button>
          </div>
          <div class="record-list">
            <div v-for="(item, idx) in inspectionRecords" :key="item.id + '-' + idx" class="record-item">
              <span class="record-status-icon" :class="item.statusType">
                <i :class="item.icon"></i>
              </span>
              <div class="record-main">
                <strong>{{ item.line }}</strong>
                <p>{{ item.progress }}</p>
              </div>
              <button type="button" class="text-link" @click="openRecordDetail('inspection', item)">
                查看详情 <i class="el-icon-right"></i>
              </button>
            </div>
          </div>
        </section>

        <section class="scene-card record-card">
          <div class="card-head">
            <div class="card-title-wrap">
              <span class="feature-icon small"><i class="el-icon-document"></i></span>
              <div>
                <h3>AI 报告记录</h3>
                <p>近一周 · 共 {{ reportTotal || 0 }} 份</p>
              </div>
            </div>
            <button type="button" class="view-all-link" @click="openDrawer('AI 报告记录')">
              查看全部 <i class="el-icon-right"></i>
            </button>
          </div>
          <div class="record-list">
            <div v-for="(item, idx) in reportRecords" :key="item.id + '-' + idx" class="record-item">
              <!-- <span class="report-type-icon" :class="item.iconType">
                <i :class="item.icon"></i>
              </span> -->
              <i class="el-icon-document drawer-header-icon"></i>
              <div class="record-main">
                <strong>{{ item.title }}</strong>
                <p>{{ item.desc }}</p>
              </div>
              <button type="button" class="text-link" @click="openRecordDetail('report', item)">
                查看详情 <i class="el-icon-right"></i>
              </button>
            </div>
          </div>
        </section>
      </div>

      <section class="scene-card line-equipment-card">
        <div class="card-head compact">
          <div class="card-title-wrap">
            <span class="line-card-icon"><i class="fa fa-sitemap"></i></span>
            <h3>产线设备</h3>
            <span class="line-total-tag">共 {{ lineDeviceTotal }} 台</span>
          </div>
          <button type="button" class="link-pill compact" @click="openLineDeviceList('ALL')">
            查看全部 <i class="el-icon-right"></i>
          </button>
        </div>

        <div class="line-type-grid">
          <div
            v-for="line in productionLineCards"
            :key="line.key"
            class="line-type-card"
            @click="openLineDeviceList(line.key)"
          >
            <span class="line-type-icon" :class="line.iconClass">
              <i :class="line.icon"></i>
            </span>
            <div class="line-type-info">
              <strong>{{ line.name }}</strong>
              <p>{{ line.desc }}</p>
            </div>
            <div class="line-type-count" :class="line.countColor">
              {{ line.count }}<em>台</em>
            </div>
          </div>
        </div>

        <div class="line-visual">
          <iframe
            class="line-iframe"
            :src="factoryIframeUrl"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </div>
      </section>
    </div>

    <template #drawer-panel>
      <div class="ema-drawer">
        <div class="iframe-drawer-header">
          <div v-if="drawerMode === 'deviceList'" class="drawer-header-main">
            <div>
              <div class="title-wrap">
                <i class="el-icon-document drawer-header-icon"></i>
                <h2>{{ drawerTitle }}</h2>
              </div>
              <p v-if="drawerSubtitle" class="drawer-header-subtitle">{{ drawerSubtitle }}</p>
            </div>
          </div>
          <button type="button" class="drawer-close" @click="closeDrawer">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div v-if="drawerMode === 'deviceList'" class="device-list-shell">
          <div class="device-list-grid">
            <aside class="device-list-nav">
              <div class="device-list-nav-label">分类</div>
              <div
                v-for="item in lineNavItems"
                :key="item.key"
                class="device-nav-item"
                :class="{ active: activeLineKey === item.id }"
                @click="activeLineKey = item.id"
              >
                <span class="device-nav-icon" :class="item.iconClass">
                  <i :class="item.icon"></i>
                </span>
                <div class="device-nav-text">
                  <div class="device-nav-name">{{ item.label }}</div>
                  <div class="device-nav-count">{{ item.count }} 台</div>
                </div>
              </div>
            </aside>

            <div class="device-list-main">
              <div class="device-list-main-head">
                <div class="device-list-main-title">设备列表</div>
                <div class="device-list-main-meta">{{ lineListMeta }}</div>
              </div>
              <div class="device-list-scroll">
                <div v-for="(device, dIdx) in filteredLineDevices" :key="device.name + '-' + dIdx" class="device-row">
                  <span class="device-row-icon">
                    <i class="fa fa-cog"></i>
                  </span>
                  <div class="device-row-info">
                    <div class="device-row-top">
                      <div class="device-row-name">{{ device.name }}</div>
                      <span class="device-status-tag" :class="device.statusType">{{ device.status }}</span>
                    </div>
                    <div class="device-row-sub">{{ device.sub }} · {{ device.model }}</div>
                  </div>
                  <button type="button" class="device-diag-btn" @click="handleDeviceDiag(device.name)">
                    <i class="fa fa-stethoscope"></i>AI 诊断
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="iframe-shell">
          <iframe
            v-if="drawerVisible"
            :src="drawerUrl"
            class="drawer-iframe"
            frameborder="0"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </template>
  </EmaAgentLayout>
</template>

<script>
import * as echarts from 'echarts'
import EmaAgentLayout from './emaAgentLayout.vue'

const EMA_DRAWER_URLS = {
  'AI 诊断': 'https://ema-cloud.cosmoplat.com/open/viewer/diagnosis-list-cosmo',
  'AI 巡检': 'https://ema-cloud.cosmoplat.com/open/viewer/inspection-list-cosmo',
  '巡检详情': 'https://ema-cloud.cosmoplat.com/open/viewer/inspection-cosmo',
  'AI 报告记录': 'https://ema-cloud.cosmoplat.com/open/viewer/ai-report-list-cosmo',
  'SMT 月度运营报告': 'https://ema-cloud.cosmoplat.com/open/viewer/report-cosmo'
}

const LINE_DEVICE_GROUPS = [
  {
    key: 'SMT',
    name: 'SMT 产线',
    icon: 'fa fa-microchip',
    iconClass: 'gradient-smt',
    devices: [
      { name: '锡膏印刷机 SP-01', model: 'DEK Horizon 03iX', sub: 'SMT-1 · 印刷工序', status: '正常', statusType: 'normal' },
      { name: '贴片机 SMT-03', model: 'YAMAHA YSM20', sub: 'SMT-1 · 贴片工序 · 主机', status: '异常', statusType: 'danger' },
      { name: '贴片机 SMT-05', model: 'YAMAHA YSM20', sub: 'SMT-2 · 贴片工序 · 副机', status: '异常', statusType: 'danger' },
      { name: '回流焊 RF-02', model: 'HELLER 1913 MK5', sub: 'SMT-1 · 回流焊接', status: '预警', statusType: 'warning' },
      { name: 'AOI 检测机 #05', model: 'Koh Young Zenith', sub: 'SMT-2 · 焊后检测', status: '预警', statusType: 'warning' },
      { name: 'ICT 测试机 #04', model: 'Teradyne TestStation', sub: 'SMT-2 · 在线测试', status: '正常', statusType: 'normal' }
    ]
  },
  {
    key: 'DIP',
    name: 'DIP 产线',
    icon: 'fa fa-cubes',
    iconClass: 'gradient-dip',
    devices: [
      { name: '波峰焊 WS-02', model: '日东 NSU-330F', sub: 'DIP-1 · 焊接工序', status: '正常', statusType: 'normal' },
      { name: 'ICT 测试机 #02', model: 'Keysight 3070', sub: 'DIP-1 · 焊后测试', status: '正常', statusType: 'normal' }
    ]
  }
]

export default {
  name: 'EmaAgent',
  components: { EmaAgentLayout },
  data() {
    return {
      agentLogo: require('@/assets/agentLogo/equipment.png'),
      drawerVisible: false,
      drawerMode: 'iframe',
      drawerTitle: 'AI 诊断',
      drawerSubtitle: '',
      drawerUrl: EMA_DRAWER_URLS['AI 诊断'],
      activeLineKey: 'ALL',
      lineDeviceGroups: LINE_DEVICE_GROUPS,
      flatGroupList: [],
      lineGroupCards: [],
      factoryIframeUrl: 'https://lingzhi-light3d.hdt.cosmoplat.com/preview/5372?type=iframe&factoryDirector=true',
      overviewStats: [],
      diagnosisSummary: { total: 0, normal: 0, abnormal: 0 },
      diagnosisTrend: [],
      healthSummary: { total: 0, avg: '-' },
      healthBars: [],
      diagnosisRecords: [],
      inspectionRecords: [],
      inspectionSummary: { totalCount: 0, normalCount: 0, abnormalCount: 0 },
      reportRecords: [],
      reportTotal: 0,
      diagnosisChart: null,
      healthChart: null,
      chartResizeObserver: null
    }
  },
  mounted() {
    Promise.all([
      this.fetchDeviceAssistant(),
      this.fetchHealthDistribution(),
      this.fetchDeviceAiDiagnosis(),
      this.fetchAiDiagnosisRecord(),
      this.fetchAiInspectionTaskRecord(),
      this.fetchFlatGroupListWithCount(),
      this.fetchGroupListWithCount(),
      this.fetchStandardReportPage()
    ]).then(() => {
      this.$nextTick(() => {
        this.initCharts()
        this.setupChartResizeObserver()
      })
    })
  },
  beforeDestroy() {
    this.teardownChartResizeObserver()
    this.disposeCharts()
  },
  computed: {
    lineDeviceTotal() {
      const allGroup = this.flatGroupList.find(g => g.groupName === '全部')
      return allGroup
        ? (allGroup.deviceAssistantCount || (allGroup.deviceAssistantList || []).length)
        : 0
    },
    lineNavItems() {
      const iconPool = [
        { icon: 'fa fa-tag', iconClass: 'gradient-smt' },
        { icon: 'fa fa-tag', iconClass: 'gradient-dip' },
        { icon: 'fa fa-tag', iconClass: 'gradient-other' },
        { icon: 'fa fa-tag', iconClass: 'gradient-other2' },
        { icon: 'fa fa-tag', iconClass: 'gradient-warn' },
        { icon: 'fa fa-tag', iconClass: 'gradient-test' },
        { icon: 'fa fa-tag', iconClass: 'gradient-data' },
        { icon: 'fa fa-tag', iconClass: 'gradient-pc' }
      ]
      // 按 id 排序后分配图标，确保与 productionLineCards 一致
      const sorted = [...this.flatGroupList].sort((a, b) => Number(a.id) - Number(b.id))
      const iconMap = {}
      sorted.forEach((g, idx) => {
        iconMap[String(g.id)] = iconPool[idx % iconPool.length]
      })
      return this.flatGroupList.map((g, idx) => {
        const color = iconMap[String(g.id)]
        return {
          key: String(g.id) + '-' + idx,
          id: String(g.id),
          label: g.groupName || '未知分组',
          count: g.deviceAssistantCount || (g.deviceAssistantList || []).length,
          icon: color.icon,
          iconClass: color.iconClass
        }
      })
    },
    filteredLineDevices() {
      // 兼容旧 activeLineKey 为 'ALL' 的情况
      const key = this.activeLineKey === 'ALL' && this.flatGroupList.length > 0
        ? String(this.flatGroupList[0].id)
        : this.activeLineKey
      const group = this.flatGroupList.find(g => String(g.id) === key)
      return group ? (group.deviceAssistantList || []).map(this._mapDeviceItem) : []
    },
    lineListMeta() {
      const key = this.activeLineKey === 'ALL' && this.flatGroupList.length > 0
        ? String(this.flatGroupList[0].id)
        : this.activeLineKey
      const group = this.flatGroupList.find(g => String(g.id) === key)
      if (!group) return ''
      const count = group.deviceAssistantCount != null
        ? group.deviceAssistantCount
        : (group.deviceAssistantList || []).length
      return `${group.groupName} · ${count} 台`
    },
    productionLineCards() {
      return this.lineGroupCards
    }
  },
  methods: {
    async fetchDeviceAssistant() {
      try {
        const res = await fetch('/dev-api/one/cosmo/deviceAssistant')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          this.overviewStats = [
            { icon: 'fa fa-cogs', value: (d.deviceCount || 0) + ' 台', label: '接入设备', type: 'primary', clickable: true, action: 'deviceList' },
            { icon: 'fa fa-heartbeat', value: (d.healthRate || 0) + '%', label: '健康率', type: 'success', delta: '', clickable: true, action: 'aiDiagnosis' },
            { icon: 'fa fa-exclamation-triangle', value: (d.abnormalDeviceCount || 0) + ' 台', label: '异常设备', type: 'warning', clickable: true, action: 'aiInspection' },
            { icon: 'fa fa-file-text-o', value: (d.reportCount || 0) + ' 份', label: '本月报告', type: 'info', clickable: true, action: 'aiReport' }
          ]
        }
      } catch (e) {
        console.error('获取设备助手信息失败', e)
      }
    },
    async fetchHealthDistribution() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getHealthDistribution')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          this.healthSummary = {
            total: d.deviceTotalCount || d.totalCount || 0,
            avg: d.averageHealthScore != null ? d.averageHealthScore.toFixed(2) : '-'
          }
          const colorMap = {
            4: '#16a34a',
            3: '#86efac',
            2: '#f59e0b',
            1: '#ef4444'
          }
          this.healthBars = (d.distribution || [])
            .sort((a, b) => b.healthType - a.healthType)
            .map(item => ({
              label: item.name || '未知',
              value: item.count,
              color: colorMap[item.healthType] || '#94a3b8',
              labelColor: colorMap[item.healthType] || '#94a3b8'
            }))
        }
      } catch (e) {
        console.error('获取健康分布失败', e)
      }
    },
    async fetchDeviceAiDiagnosis() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getDeviceAiDiagnosis')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          this.diagnosisSummary = {
            total: d.allReportCount || 0,
            normal: d.normalCount || 0,
            abnormal: d.abnormalCount || 0
          }
          // 合并三条趋势线
          const totalMap = {}
          const normalMap = {}
          const abnormalMap = {}
          ;(d.totalTrend || []).forEach(item => {
            totalMap[item.statisticsDate] = item.reportCount
          })
          ;(d.normalTrend || []).forEach(item => {
            normalMap[item.statisticsDate] = item.reportCount
          })
          ;(d.abnormalTrend || []).forEach(item => {
            abnormalMap[item.date] = item.count
          })
          // 以 totalTrend 的日期为基准
          this.diagnosisTrend = (d.totalTrend || []).map(item => ({
            date: item.statisticsDate,
            total: item.reportCount,
            normal: normalMap[item.statisticsDate] || 0,
            abnormal: abnormalMap[item.statisticsDate] || 0
          }))
        }
      } catch (e) {
        console.error('获取设备AI诊断失败', e)
      }
    },
    async fetchAiDiagnosisRecord() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getAiDiagnosisRecord?pageSize=5&pageNum=1')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          // 也更新诊断摘要（该接口也返回统计数）
          if (d.weeklyReportCount != null) {
            this.diagnosisSummary = {
              total: d.weeklyReportCount || 0,
              normal: d.normalCount || 0,
              abnormal: d.abnormalCount || 0
            }
          }
          // 合并趋势数据
          const totalMap = {}
          const normalMap = {}
          const abnormalMap = {}
          ;(d.totalTrend || []).forEach(item => {
            totalMap[item.statisticsDate] = item.reportCount
          })
          ;(d.normalTrend || []).forEach(item => {
            normalMap[item.statisticsDate] = item.reportCount
          })
          ;(d.abnormalTrend || []).forEach(item => {
            abnormalMap[item.date] = item.count
          })
          if (d.totalTrend && d.totalTrend.length > 0) {
            this.diagnosisTrend = d.totalTrend.map(item => ({
              date: item.statisticsDate,
              total: item.reportCount,
              normal: normalMap[item.statisticsDate] || 0,
              abnormal: abnormalMap[item.statisticsDate] || 0
            }))
          }
          // 诊断记录列表
          const statusMap = {
            NORMAL: { status: '正常', statusType: 'success', icon: 'el-icon-circle-check' },
            SLIGHT_ABNORMAL: { status: '轻微异常', statusType: 'danger', icon: 'el-icon-warning' },
            NEED_ATTENTION: { status: '需关注', statusType: 'danger', icon: 'el-icon-warning' },
            SEVERE_ABNORMAL: { status: '严重异常', statusType: 'danger', icon: 'el-icon-warning' }
          }
          this.diagnosisRecords = (d.recordVOList || []).slice(0, 5).map(record => {
            const st = statusMap[record.healthStatus] || { status: record.healthStatus || '未知', statusType: 'muted', icon: 'el-icon-info' }
            return {
              id: record.id,
              device: record.deviceName || '未知设备',
              issue: record.faultName || record.jobName || '—',
              status: st.status,
              statusType: st.statusType,
              icon: st.icon,
              bizId: record.bizId || '',
              ruleId: record.ruleId || ''
            }
          })
        }
      } catch (e) {
        console.error('获取AI诊断记录失败', e)
      }
    },
    async fetchAiInspectionTaskRecord() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getAIInspectionTaskRecord?pageSize=5&pageNum=1')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          this.inspectionSummary = {
            totalCount: d.weeklyInspectionCount || 0,
            normalCount: d.normalDeviceCount || 0,
            abnormalCount: d.abnormalDeviceCount || 0
          }
          const statusMap = {
            0: { statusType: 'success', icon: 'el-icon-circle-check' },
            1: { statusType: 'danger', icon: 'el-icon-warning' }
          }
          this.inspectionRecords = (d.inspectionRecordList?.list || []).slice(0, 5).map(record => {
            const st = statusMap[record.diagnosisStatus] || { statusType: 'muted', icon: 'el-icon-time' }
            const timeStr = record.startTime
              ? record.startTime.slice(11, 16)
              : ''
            const totalDev = record.totalDevices || 0
            const progress = timeStr
              ? `${totalDev} 台 · ${timeStr}`
              : `${totalDev} 台`
            return {
              id: record.taskId || record.bizId,
              line: record.jobName || record.station || '未知任务',
              progress,
              statusType: st.statusType,
              icon: st.icon,
              bizId: record.bizId || ''
            }
          })
        }
      } catch (e) {
        console.error('获取AI巡检记录失败', e)
      }
    },
    async fetchFlatGroupListWithCount() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getFlatGroupListWithCount')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          this.flatGroupList = result.data
        }
      } catch (e) {
        console.error('获取扁平分组列表失败', e)
      }
    },
    async fetchGroupListWithCount() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getGroupListWithCount')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const iconColors = [
            { icon: 'fa fa-tag', iconClass: 'gradient-dip' },
            { icon: 'fa fa-tag', iconClass: 'gradient-other' },
            { icon: 'fa fa-tag', iconClass: 'gradient-other2' },
            { icon: 'fa fa-tag', iconClass: 'gradient-warn' },
            { icon: 'fa fa-tag', iconClass: 'gradient-test' },
            { icon: 'fa fa-tag', iconClass: 'gradient-data' },
            { icon: 'fa fa-tag', iconClass: 'gradient-pc' },
            { icon: 'fa fa-tag', iconClass: 'gradient-smt' }
          ]
          const countColors = ['primary', 'green', 'orange', 'purple', 'teal', 'red']
          // 按 id 排序后分配图标，确保与 lineNavItems 一致
          const sorted = [...result.data].sort((a, b) => Number(a.id) - Number(b.id))
          const iconMap = {}
          sorted.forEach((group, idx) => {
            iconMap[String(group.id)] = iconColors[idx % iconColors.length]
          })
          this.lineGroupCards = result.data.map((group, idx) => {
            const color = iconMap[String(group.id)] || iconColors[0]
            return {
              key: String(group.id) + '-' + idx,
              name: group.groupName || '未知分组',
              desc: `${group.deviceAssistantCount || (group.deviceAssistantList || []).length} 台设备`,
              count: group.deviceAssistantCount || (group.deviceAssistantList || []).length,
              icon: color.icon,
              iconClass: color.iconClass,
              countColor: countColors[Math.abs(Number(group.id) || 0) % countColors.length]
            }
          })
        }
      } catch (e) {
        console.error('获取产线分组失败', e)
      }
    },
    async fetchStandardReportPage() {
      try {
        const res = await fetch('/dev-api/one/cosmo/getStandardReportPage?pageSize=5&pageNum=1')
        const result = await res.json()
        if (result.code === 0 && result.data) {
          const d = result.data
          const pr = d.pageResult || {}
          this.reportTotal = d.weeklyReportCount || 0
          const iconPool = [
            { icon: 'el-icon-document', iconType: 'blue' },
            { icon: 'el-icon-document-checked', iconType: 'green' },
            { icon: 'el-icon-warning', iconType: 'red' },
            { icon: 'el-icon-s-data', iconType: 'orange' },
            { icon: 'el-icon-box', iconType: 'teal' },
            { icon: 'el-icon-odometer', iconType: 'purple' }
          ]
          this.reportRecords = (pr.list || []).slice(0, 5).map((report, idx) => {
            const iconStyle = iconPool[idx % iconPool.length]
            const timeStr = report.createTime
              ? report.createTime.slice(5, 16).replace('T', ' ')
              : ''
            return {
              id: report.id,
              title: report.reportName || '未知报告',
              desc: timeStr ? `${timeStr} · ${report.operator || '系统'}` : '—',
              icon: iconStyle.icon,
              iconType: iconStyle.iconType,
              bizId: report.bizId || ''
            }
          })
        }
      } catch (e) {
        console.error('获取报告记录失败', e)
      }
    },
    _mapDeviceItem(device) {
      return {
        name: device.deviceName || '未知设备',
        model: device.deviceModel || '',
        sub: device.groupName || device.factoryName || '',
        status: '正常',
        statusType: 'normal'
      }
    },
    formatNumber(value) {
      return Number(value).toLocaleString('en-US')
    },
    initCharts() {
      this.initDiagnosisChart()
      this.initHealthChart()
    },
    initDiagnosisChart() {
      const el = this.$refs.diagnosisChartRef
      if (!el) return
      if (this.diagnosisChart) this.diagnosisChart.dispose()
      this.diagnosisChart = echarts.init(el)
      const dates = this.diagnosisTrend.map(item => item.date)
      this.diagnosisChart.setOption({
        color: ['#4a5ff3', '#22c55e', '#ef4444'],
        grid: { left: 4, right: 4, top: 34, bottom: 4, containLabel: true },
        legend: {
          right: 0,
          top: 0,
          itemWidth: 8,
          itemHeight: 8,
          itemGap: 14,
          icon: 'circle',
          textStyle: { color: '#8fa0b8', fontSize: 11, fontWeight: 600 },
          data: ['总次数', '正常', '异常']
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#94a3b8', fontSize: 11, margin: 10 }
        },
        yAxis: {
          type: 'value',
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { lineStyle: { color: '#eef2f7', type: 'solid' }}
        },
        series: [
          {
            name: '总次数',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { width: 2.5, color: '#4a5ff3' },
            itemStyle: { color: '#4a5ff3', borderColor: '#fff', borderWidth: 2 },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(74, 95, 243, 0.22)' },
                { offset: 1, color: 'rgba(74, 95, 243, 0.02)' }
              ])
            },
            data: this.diagnosisTrend.map(item => item.total)
          },
          {
            name: '正常',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 2, color: '#22c55e' },
            itemStyle: { color: '#22c55e', borderColor: '#fff', borderWidth: 2 },
            data: this.diagnosisTrend.map(item => item.normal)
          },
          {
            name: '异常',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 2, color: '#ef4444', type: 'dashed' },
            itemStyle: { color: '#ef4444', borderColor: '#fff', borderWidth: 2 },
            data: this.diagnosisTrend.map(item => item.abnormal)
          }
        ]
      })
    },
    initHealthChart() {
      const el = this.$refs.healthChartRef
      if (!el) return
      if (this.healthChart) this.healthChart.dispose()
      this.healthChart = echarts.init(el)
      this.healthChart.setOption({
        grid: { left: 0, right: 8, top: 6, bottom: 0, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: [0.02, 0.16],
          data: this.healthBars.map(item => item.label),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#64748b',
            fontSize: 11,
            margin: 10,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          show: false,
          max: (extent) => Math.ceil(extent.max * 1.18) || 10
        },
        series: [
          {
            type: 'bar',
            barWidth: '62%',
            barMaxWidth: 72,
            data: this.healthBars.map(bar => ({
              value: bar.value,
              itemStyle: {
                color: bar.color,
                borderRadius: [8, 8, 0, 0]
              },
              label: {
                show: true,
                position: 'top',
                fontSize: 13,
                fontWeight: 700,
                color: bar.labelColor || bar.color,
                distance: 6
              }
            }))
          }
        ]
      })
    },
    setupChartResizeObserver() {
      if (this.chartResizeObserver) return
      this.chartResizeObserver = new ResizeObserver(() => {
        this.diagnosisChart && this.diagnosisChart.resize()
        this.healthChart && this.healthChart.resize()
      })
      const el1 = this.$refs.diagnosisChartRef
      const el2 = this.$refs.healthChartRef
      if (el1) this.chartResizeObserver.observe(el1)
      if (el2) this.chartResizeObserver.observe(el2)
    },
    teardownChartResizeObserver() {
      if (this.chartResizeObserver) {
        this.chartResizeObserver.disconnect()
        this.chartResizeObserver = null
      }
    },
    disposeCharts() {
      if (this.diagnosisChart) {
        this.diagnosisChart.dispose()
        this.diagnosisChart = null
      }
      if (this.healthChart) {
        this.healthChart.dispose()
        this.healthChart = null
      }
    },
    sendAISummary(type) {
      const texts = {
        diagnosis: '对近 7 天的诊断数据做 AI 总结，给出趋势与异常归因',
        health: '全厂设备健康度分布做 AI 总结与改善建议'
      }
      const content = texts[type]
      if (!content) return
      this.sendChatMessage(content)
    },
    handleStatClick(stat) {
      if (!stat || !stat.clickable) return
      switch (stat.action) {
        case 'deviceList':
          this.openLineDeviceList()
          break
        case 'aiDiagnosis':
          this.openDrawer('AI 诊断')
          break
        case 'aiInspection':
          this.openDrawer('AI 巡检')
          break
        case 'aiReport':
          this.openDrawer('AI 报告记录')
          break
      }
    },
    openLineDeviceList(initialKey) {
      const parts = this.flatGroupList.map(g =>
        `${g.groupName} ${g.deviceAssistantCount || (g.deviceAssistantList || []).length} 台`
      )
      this.drawerMode = 'deviceList'
      this.drawerTitle = '产线设备清单'
      this.drawerSubtitle = `${parts.join(' · ')} · 共 ${this.lineDeviceTotal} 台`
      // 解析原始 id（去掉可能有的 -idx 后缀）
      const rawKey = initialKey ? initialKey.replace(/-\d+$/, '') : ''
      const exists = rawKey && this.flatGroupList.some(g => String(g.id) === rawKey)
      this.activeLineKey = exists ? rawKey : String(this.flatGroupList[0]?.id || 'ALL')
      this.drawerVisible = true
    },
    handleDeviceDiag(deviceName) {
      const prompt = `帮我诊断 ${deviceName} 的设备健康情况`
      this.closeDrawer()
      this.sendChatMessage(prompt)
    },
    sendChatMessage(content) {
      if (!content) return
      this.$store.dispatch('cosmoClaw/createNewSession', { agentName: 'main', forceNew: true }).then((sessionKey) => {
        setTimeout(() => {
          this.$store.dispatch('cosmoClaw/sendMessage', { content, sessionKey })
        }, 500)
      })
    },
    openDrawer(title) {
      this.drawerMode = 'iframe'
      this.drawerSubtitle = ''
      this.drawerTitle = title
      this.drawerUrl = EMA_DRAWER_URLS[title] || 'https://ema-cloud.cosmoplat.com/open/viewer/report-cosmo'
      this.drawerVisible = true
    },
    openRecordDetail(type, record) {
      if (type === 'diagnosis') {
        const params = new URLSearchParams()
        if (record.bizId) params.set('bizId', record.bizId)
        if (record.ruleId) params.set('ruleId', record.ruleId)
        this.drawerUrl = 'https://ema-cloud.cosmoplat.com/open/viewer/diagnosis-cosmo?' + params.toString()
        this.drawerTitle = 'AI 诊断详情'
      } else if (type === 'inspection') {
        const params = new URLSearchParams()
        if (record.bizId) params.set('bizId', record.bizId)
        this.drawerUrl = 'https://ema-cloud.cosmoplat.com/open/viewer/inspection-cosmo?' + params.toString()
        this.drawerTitle = 'AI 巡检详情'
      } else if (type === 'report') {
        const params = new URLSearchParams()
        if (record.bizId) params.set('bizId', record.bizId)
        this.drawerUrl = 'https://ema-cloud.cosmoplat.com/open/viewer/report-cosmo?' + params.toString()
        this.drawerTitle = 'AI 报告详情'
      }
      this.drawerMode = 'iframe'
      this.drawerSubtitle = ''
      this.drawerVisible = true
    },
    closeDrawer() {
      this.drawerVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: #4a5ff3;
$primary-soft: #eef2ff;
$text-main: #1f2a44;
$text-muted: #8fa0b8;

.ema-board {
  width: 100%;
}

.section-title {
  margin-bottom: 14px;
  color: $text-main;
  font-size: 14px;
  font-weight: 700;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.overview-stat-card,
.scene-card {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.86);
  box-shadow: 0 12px 40px rgba(69, 96, 178, 0.12);
}

.overview-stat-card {
  min-height: 88px;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;

  &.clickable {
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 16px 44px rgba(69, 96, 178, 0.16);
    }
  }
}

.overview-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &.primary { background: $primary-soft; color: $primary; }
  &.success { background: #e8f8ef; color: #1a8a4a; }
  &.warning { background: #fff7ed; color: #ea580c; }
  &.info { background: #eff6ff; color: #2563eb; }
}

.overview-stat-value {
  color: $text-main;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.overview-stat-delta {
  margin-left: 6px;
  font-style: normal;
  font-size: 12px;
  font-weight: 700;
  color: #1a8a4a;
}

.overview-stat-label {
  margin-top: 4px;
  color: $text-muted;
  font-size: 12px;
  font-weight: 600;
}

.chart-grid,
.record-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 20px;
}

.chart-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.record-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.scene-card {
  border-radius: 20px;
  padding: 20px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  &.compact {
    margin-bottom: 12px;
  }
}

.card-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  h3 {
    margin: 0;
    color: $text-main;
    font-size: 15px;
    font-weight: 700;
  }

  p {
    margin: 4px 0 0;
    color: $text-muted;
    font-size: 12px;
    font-weight: 600;
  }
}

.chart-card {
  padding: 18px 20px 16px;
}

.card-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.period-tag {
  color: $text-muted;
  font-size: 12px;
  font-weight: 600;
}

.feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  &.diagnosis {
    background: linear-gradient(135deg, #4e86ff 0%, $primary 100%);
    box-shadow: 0 8px 18px rgba(58, 108, 219, 0.18);
  }

  &.health {
    background: linear-gradient(135deg, #34d399 0%, #16a34a 100%);
    box-shadow: 0 8px 18px rgba(22, 163, 74, 0.18);
  }

  &.small {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    font-size: 16px;
    background: linear-gradient(135deg, #4e86ff 0%, $primary 100%);
    box-shadow: 0 8px 18px rgba(58, 108, 219, 0.18);
  }
}

.metric-boxes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  &.metric-boxes-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.metric-box {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.metric-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $text-muted;
  font-size: 12px;
  font-weight: 600;
}

.metric-value {
  display: block;
  margin-top: 6px;
  color: $text-main;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;

  &.success { color: #16a34a; }
  &.danger { color: #ef4444; }
}

.chart-panel {
  position: relative;
  padding-top: 4px;
}

.chart-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.chart-panel-title {
  color: $text-main;
  font-size: 13px;
  font-weight: 700;
}

.chart-panel-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  color: #8fa0b8;
  font-size: 11px;
  font-weight: 600;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
}

.echart-container {
  width: 100%;
  height: 220px;
}

.dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;

  &.success { background: #22c55e; }
  &.danger { background: #ef4444; }
}

.link-pill {
  border: 0;
  border-radius: 999px;
  background: $primary-soft;
  color: $primary;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &.compact {
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    gap: 2px;
  }
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid rgba(226, 232, 240, 0.78);
  border-radius: 14px;
  background: #fff;

  strong {
    color: $text-main;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    margin: 4px 0 0;
    color: $text-muted;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 600;
  }
}

.record-card .card-title-wrap h3 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-main {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.record-status-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;

  &.success { background: #e8f8ef; color: #16a34a; }
  &.warning { background: #fff7ed; color: #ea580c; }
  &.danger { background: #fee2e2; color: #dc2626; }
  &.info { background: $primary-soft; color: $primary; }
  &.muted { background: #f1f5f9; color: #94a3b8; }
}

.report-type-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  color: #fff;

  &.blue { background: linear-gradient(135deg, #4e86ff 0%, $primary 100%); }
  &.green { background: linear-gradient(135deg, #34d399 0%, #16a34a 100%); }
  &.red { background: linear-gradient(135deg, #f87171 0%, #ef4444 100%); }
  &.orange { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); }
  &.teal { background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%); }
  &.purple { background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%); }
}

.view-all-link {
  border: 0;
  background: transparent;
  color: $primary;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
}

.status-tag {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;

  &.success { background: #e8f8ef; color: #1a8a4a; }
  &.warning { background: #fff7ed; color: #ea580c; }
  &.danger { background: #fee2e2; color: #dc2626; }
}

.text-link {
  border: 0;
  background: transparent;
  color: $primary;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
}

.line-equipment-card {
  padding-bottom: 24px;
  overflow: hidden;
}

.line-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: $primary-soft;
  color: $primary;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}

.line-total-tag {
  color: $text-muted;
  font-size: 10px;
  font-weight: 600;
}

.line-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.line-type-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
  background: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #fff;
    border-color: rgba($primary, 0.3);
  }
}

.line-type-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  color: #fff;

  &.gradient-smt {
    background: linear-gradient(135deg, #4e86ff, #4a5ff3);
    box-shadow: 0 6px 14px rgba(74, 95, 243, 0.22);
  }

  &.gradient-dip {
    background: linear-gradient(135deg, #52c41a, #1a8a4a);
    box-shadow: 0 6px 14px rgba(82, 196, 26, 0.22);
  }

  &.gradient-other {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    box-shadow: 0 6px 14px rgba(245, 158, 11, 0.22);
  }

  &.gradient-other2 {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    box-shadow: 0 6px 14px rgba(139, 92, 246, 0.22);
  }

  &.gradient-warn {
    background: linear-gradient(135deg, #fb923c, #ea580c);
    box-shadow: 0 6px 14px rgba(251, 146, 60, 0.22);
  }

  &.gradient-test {
    background: linear-gradient(135deg, #06b6d4, #0891b2);
    box-shadow: 0 6px 14px rgba(6, 182, 212, 0.22);
  }

  &.gradient-data {
    background: linear-gradient(135deg, #f472b6, #db2777);
    box-shadow: 0 6px 14px rgba(244, 114, 182, 0.22);
  }

  &.gradient-pc {
    background: linear-gradient(135deg, #78716c, #57534e);
    box-shadow: 0 6px 14px rgba(120, 113, 108, 0.22);
  }
}

.line-type-info {
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    color: #334155;
    font-size: 13px;
    font-weight: 600;
  }

  p {
    margin: 2px 0 0;
    color: #94a3b8;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.line-type-count {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;

  &.primary { color: $primary; }
  &.green { color: #1a8a4a; }

  em {
    margin-left: 2px;
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
  }
}

.line-visual {
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
  background: rgba(15, 23, 42, 0.05);
}

.line-iframe {
  width: 100%;
  height: 360px;
  border: 0;
  display: block;
  background: #0f172a;
}

.iframe-shell,
.drawer-iframe {
  height: 100%;
  width: 100%;
}

.ema-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.iframe-drawer-header {
  position: relative;
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: 18px;
    color: $text-main;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.drawer-header-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 20px 24px;
  border-bottom: 1px solid #eef2f7;
  padding-right: 68px;
}

.drawer-header-icon {
  margin-top: 2px;
  font-size: 16px;
  color: $primary;
}

.drawer-header-subtitle {
  margin: 4px 0 0;
  font-size: 11px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.iframe-shell {
  flex: 1;
  min-height: 0;
}

.drawer-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.device-list-shell {
  flex: 1;
  min-height: 0;
  padding: 16px 20px 20px;
}

.device-list-grid {
  height: 100%;
  display: grid;
  grid-template-columns: 180px 1fr;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.device-list-nav {
  border-right: 1px solid #f1f5f9;
  background: rgba(248, 250, 252, 0.5);
  padding: 12px;
  overflow-y: auto;
}

.device-list-nav-label {
  padding: 0 8px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
}

.device-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
  }

  &.active {
    background: $primary-soft;
    border-color: rgba(74, 95, 243, 0.2);
    color: $primary;

    .device-nav-count {
      color: rgba(74, 95, 243, 0.7);
    }

    .device-nav-icon.icon-all {
      background: #fff;
      color: $primary;
    }
  }
}

.device-nav-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 12px;
  background: #f1f5f9;
  color: #64748b;

  &.gradient-smt {
    background: linear-gradient(135deg, #4e86ff, #4a5ff3);
    color: #fff;
  }

  &.gradient-dip {
    background: linear-gradient(135deg, #52c41a, #1a8a4a);
    color: #fff;
  }

  &.gradient-other {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #fff;
  }

  &.gradient-other2 {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
    color: #fff;
  }

  &.gradient-warn {
    background: linear-gradient(135deg, #fb923c, #ea580c);
    color: #fff;
  }

  &.gradient-test {
    background: linear-gradient(135deg, #06b6d4, #0891b2);
    color: #fff;
  }

  &.gradient-data {
    background: linear-gradient(135deg, #f472b6, #db2777);
    color: #fff;
  }

  &.gradient-pc {
    background: linear-gradient(135deg, #78716c, #57534e);
    color: #fff;
  }
}

.device-nav-text {
  min-width: 0;
}

.device-nav-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}

.device-nav-count {
  margin-top: 4px;
  font-size: 10px;
  color: #94a3b8;
  line-height: 1;
}

.device-list-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.device-list-main-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.device-list-main-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}

.device-list-main-meta {
  font-size: 11px;
  color: #94a3b8;
}

.device-list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(74, 95, 243, 0.22) transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(74, 95, 243, 0.22);
    border-radius: 999px;
  }
}

.device-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 6px;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  background: #fff;
}

.device-row-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f8fafc;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.device-row-info {
  flex: 1;
  min-width: 0;
}

.device-row-top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.device-row-name {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-status-tag {
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;

  &.normal {
    color: #52c41a;
    background: #e8f8ef;
  }

  &.danger {
    color: #ff4d4f;
    background: #fff1f0;
  }

  &.warning {
    color: #d46b08;
    background: #fef3e8;
  }
}

.device-row-sub {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.device-diag-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 0;
  border-radius: 999px;
  background: $primary-soft;
  color: $primary;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  i {
    font-size: 10px;
  }

  &:hover {
    background: #dee6ff;
  }
}
.title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

@media (max-width: 1200px) {
  .overview-stats,
  .record-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .overview-stats,
  .record-grid,
  .line-type-grid,
  .bar-chart {
    grid-template-columns: 1fr;
  }
}
</style>
